/**
 * TypeScript types for Creative Studio API.
 *
 * Creative Studio enables AI-powered image generation and editing
 * using Google's Gemini 3 Pro Image Preview model.
 */

// =============================================================================
// Enums / Literal Types
// =============================================================================

/**
 * Supported aspect ratios for image generation.
 * Corresponds to Gemini 3 Pro Image Preview supported ratios.
 */
export type AspectRatio =
  | '1:1'
  | '16:9'
  | '9:16'
  | '4:5'
  | '5:4'
  | '4:3'
  | '3:4'
  | '3:2'
  | '2:3'
  | '21:9';

/**
 * Supported image resolutions.
 * Gemini 3 Pro supports 1K, 2K, and 4K generation.
 */
export type ImageResolution = '1K' | '2K' | '4K';

/**
 * Status of an image generation job.
 */
export type GenerationStatus = 'pending' | 'generating' | 'completed' | 'failed';

// =============================================================================
// Core Types
// =============================================================================

/**
 * A single generated image.
 */
export interface GeneratedImage {
  index: number;
  gcs_path: string;
  signed_url: string;
  width: number;
  height: number;
  mime_type: string;
  aspect_ratio: string;
  resolution: string;
}

/**
 * Response for starting an async generation job.
 */
export interface GenerationJobResponse {
  job_id: string;
  status: GenerationStatus;
  message?: string;
  created_at: string;
  estimated_seconds?: number;
}

/**
 * Response with generation results when polling.
 */
export interface GenerationResultResponse {
  job_id: string;
  status: GenerationStatus;
  images?: GeneratedImage[];
  text_response?: string;
  error_message?: string;
  generation_time_seconds?: number;
  created_at: string;
  completed_at?: string;
}

/**
 * A single item in the refinement history.
 */
export interface EditHistoryItem {
  index: number;
  instruction?: string;
  image: GeneratedImage;
  timestamp: string;
}

/**
 * Maximum length of a user-supplied reference image name (matches backend
 * REFERENCE_IMAGE_NAME_MAX_LENGTH). The React UI no longer surfaces a
 * rename affordance and lets the server assign sequential names
 * (`ref-image-N`, `ref-mark-N`); the cap stays in place to bound
 * the field for any non-FE caller that still supplies a name.
 */
export const REFERENCE_IMAGE_NAME_MAX_LENGTH = 32;

/**
 * Marker color for a session reference mark. Auto-picked by MarkerOverlay
 * based on the brightness of the image region under the cursor —
 * 'yellow' (highlighter yellow) on dark regions, 'magenta' on light —
 * so the stroke is visible regardless of the underlying image. Locked
 * at pointerdown and sent to the backend so the refinement prompt can
 * describe the matching color.
 */
export type MarkShade = 'yellow' | 'magenta';

/**
 * A reference image with its display name and resolved signed URL. Returned
 * inside RefinementSessionResponse and used everywhere the UI renders
 * reference image thumbnails plus their labels.
 *
 * For session-only reference marks (annotation overlays the user drew on
 * top of the current image), `is_session_mark` is true and `mark_shade`
 * records which color the user picked so the UI can render a small badge.
 */
export interface ReferenceImageInfo {
  media_image_id: string;
  name: string;
  signed_url?: string | null;
  is_session_mark?: boolean;
  mark_shade?: MarkShade | null;
}

/**
 * Input shape for selecting a reference image with an optional name. Sent on:
 *   POST /generate
 *   POST /sessions/refine (start refinement session — distinct from
 *     POST /sessions/{id}/refine which applies a critique and takes
 *     ApplyRefinementRequest, NOT this shape)
 *   POST /sessions/{id}/reference-images (add to active session)
 */
export interface ReferenceImageInput {
  media_image_id: string;
  name?: string | null;
}

/**
 * Response for a refinement session with additional context.
 *
 * `brightness_grid` is a square row-major luminance matrix (values in
 * [0, 1]) of the current_image, used by MarkerOverlay to pick a
 * contrasting marker color (yellow on dark cells, magenta on light) as
 * the user hovers. Grid resolution is set by the backend (currently
 * 64×64) and read dynamically on the FE via `.length`. Null when the
 * backend couldn't decode the image — the UI falls back to a single
 * neutral color.
 */
export interface RefinementSessionResponse {
  session_id: string;
  original_prompt: string;
  reference_images: ReferenceImageInfo[];
  current_image: GeneratedImage;
  edit_history: EditHistoryItem[];
  refinement_count: number;
  max_refinements: number;
  can_refine: boolean;
  created_at: string;
  last_activity: string;
  expires_at: string;
  brightness_grid?: number[][] | null;
}

/**
 * Response after saving to Media Library.
 */
export interface SaveToMediaLibraryResponse {
  success: boolean;
  image_id?: string;
  signed_url?: string;
  message: string;
}

// =============================================================================
// Request Types
// =============================================================================

/**
 * Request to generate a new image from a text prompt.
 */
export interface GenerateImageRequest {
  prompt: string;
  aspect_ratio?: AspectRatio;
  resolution?: ImageResolution;
  reference_images?: ReferenceImageInput[];
  enable_grounding?: boolean;
}

/**
 * Request to start a multi-turn refinement session.
 *
 * Source the initial ("Original") image from either a completed
 * generation job OR a Media Library image — set exactly one of
 * `generation_job_id` or `media_image_id`. The backend rejects both-set
 * and neither-set with a 422 (`StartRefinementSessionRequest` validator).
 */
export type StartRefinementSessionRequest =
  | {
      generation_job_id: string;
      media_image_id?: never;
      reference_images?: ReferenceImageInput[];
      image_index?: number;
    }
  | {
      generation_job_id?: never;
      media_image_id: string;
      reference_images?: ReferenceImageInput[];
    };

/**
 * Request to apply a refinement critique to the current image.
 */
export interface ApplyRefinementRequest {
  critique: string;
  aspect_ratio?: AspectRatio;
  resolution?: ImageResolution;
}

/**
 * Request body for POST /sessions/{id}/reference-images.
 */
export interface AddSessionReferenceImageRequest {
  media_image_id: string;
  name?: string | null;
}

/**
 * Request body for PATCH /sessions/{id}/reference-images/{media_image_id}.
 */
export interface RenameSessionReferenceImageRequest {
  name: string;
}

/**
 * Request to save a generated/edited image to the Media Library.
 */
export interface SaveToMediaLibraryRequest {
  session_id?: string;
  generation_job_id?: string;
  image_index?: number;
  folder_id?: string;
  alt_text?: string;
  description?: string;
  tag_ids?: string[];
  filename?: string;
  /** Library category. Omit to default to 'approved'. */
  category?: 'reference' | 'approved';
}

// =============================================================================
// Ad Format Presets
// =============================================================================

/**
 * A preset for common ad formats.
 */
export interface AdFormatPreset {
  id: string;
  name: string;
  aspect_ratio: AspectRatio;
  recommended_resolution: ImageResolution;
  description: string;
}

/**
 * Predefined ad format presets for quick selection.
 */
export const AD_FORMAT_PRESETS: AdFormatPreset[] = [
  {
    id: 'youtube_thumbnail',
    name: 'YouTube Thumbnail',
    aspect_ratio: '16:9',
    recommended_resolution: '2K',
    description: 'YouTube video thumbnails and display ads',
  },
  {
    id: 'instagram_story',
    name: 'Instagram/TikTok Story',
    aspect_ratio: '9:16',
    recommended_resolution: '2K',
    description: 'Vertical stories and reels',
  },
  {
    id: 'facebook_feed',
    name: 'Facebook/Instagram Feed',
    aspect_ratio: '1:1',
    recommended_resolution: '1K',
    description: 'Square posts for social feeds',
  },
  {
    id: 'instagram_portrait',
    name: 'Instagram Portrait',
    aspect_ratio: '4:5',
    recommended_resolution: '1K',
    description: 'Portrait posts optimized for Instagram',
  },
  {
    id: 'google_display',
    name: 'Google Display Network',
    aspect_ratio: '16:9',
    recommended_resolution: '1K',
    description: 'Responsive display ads for GDN',
  },
  {
    id: 'google_discovery',
    name: 'Google Discovery',
    aspect_ratio: '4:3',
    recommended_resolution: '1K',
    description: 'Discovery and Demand Gen campaigns',
  },
  {
    id: 'ultra_wide_banner',
    name: 'Ultra-Wide Banner',
    aspect_ratio: '21:9',
    recommended_resolution: '2K',
    description: 'Wide banner ads and hero images',
  },
];

/**
 * Aspect ratio options for Google Ads campaigns.
 *
 * Based on Google Ads image asset requirements:
 * - 1:1 (Square) - Required for all campaign types
 * - 1.91:1 (Landscape) - Required for Search and Responsive Display
 * - 4:5 (Portrait) - Optional for Responsive Display Ads
 * - 16:9 (Landscape) - Performance Max campaigns
 *
 * @see https://support.google.com/google-ads/answer/13676244
 */
export const ASPECT_RATIO_OPTIONS: { value: AspectRatio; label: string; description: string }[] = [
  {
    value: '1:1',
    label: 'Square (1:1)',
    description: 'Required for all Google Ads campaigns. Recommended: 1200×1200px',
  },
  {
    value: '16:9',
    label: 'Landscape (16:9)',
    description: 'Performance Max & Display. Recommended: 1200×628px',
  },
  {
    value: '4:5',
    label: 'Portrait (4:5)',
    description: 'Optional for Responsive Display. Recommended: 1200×1500px',
  },
  {
    value: '4:3',
    label: 'Landscape (4:3)',
    description: 'Demand Gen & Discovery campaigns',
  },
  {
    value: '9:16',
    label: 'Portrait (9:16)',
    description: 'Vertical video thumbnails & YouTube Shorts',
  },
];

/**
 * Resolution options with display labels.
 */
export const RESOLUTION_OPTIONS: { value: ImageResolution; label: string; description: string }[] =
  [
    { value: '1K', label: '1K (1024px)', description: 'Standard quality, fastest generation' },
    { value: '2K', label: '2K (2048px)', description: 'High quality, recommended for ads' },
    { value: '4K', label: '4K (4096px)', description: 'Ultra-high quality, for print/hero' },
  ];

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get dimensions for an aspect ratio and resolution.
 */
export function getAspectRatioDimensions(
  aspectRatio: AspectRatio,
  resolution: ImageResolution
): { width: number; height: number } {
  const baseDims: Record<ImageResolution, number> = {
    '1K': 1024,
    '2K': 2048,
    '4K': 4096,
  };
  const base = baseDims[resolution];

  const ratioMap: Record<AspectRatio, [number, number]> = {
    '1:1': [1, 1],
    '16:9': [16, 9],
    '9:16': [9, 16],
    '4:5': [4, 5],
    '5:4': [5, 4],
    '4:3': [4, 3],
    '3:4': [3, 4],
    '3:2': [3, 2],
    '2:3': [2, 3],
    '21:9': [21, 9],
  };

  const [wRatio, hRatio] = ratioMap[aspectRatio];

  if (wRatio >= hRatio) {
    return {
      width: base,
      height: Math.round((base * hRatio) / wRatio),
    };
  } else {
    return {
      width: Math.round((base * wRatio) / hRatio),
      height: base,
    };
  }
}

/**
 * Find a preset by aspect ratio.
 */
export function findPresetByAspectRatio(aspectRatio: AspectRatio): AdFormatPreset | undefined {
  return AD_FORMAT_PRESETS.find((p) => p.aspect_ratio === aspectRatio);
}
